import { ref, computed, watch } from 'vue';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import {debounce} from "lodash";

export function initializeFilters(columns) {
    const initial = { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
    columns.forEach(col => {
        if (col.filter) {
            initial[col.field] = getFilterModelBase(col.filters);
        }
    });
    return initial;
}

function getFilterModelBase(filtersParam) {
    if (filtersParam.includes('number')) {
        return { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] };
    }
    if (filtersParam.includes('date')) {
        return { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] };
    }
    if (filtersParam.includes('select')) {
        return { value: null, matchMode: FilterMatchMode.IN };
    }
    return { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] };
}

function getSelectFilterModel(filtersParam) {
    return { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.IN }] };
}

export function getFilterMatchModeOptionsBase(col, advancedFilter = false) {
    if (col.filter) {
        if (col.filters.includes('number')) {
            return [
                { label: 'Greater than', value: 'gt' },
                { label: 'Less than', value: 'lt' },
                { label: 'Equals', value: 'equals' },
            ];
        }
        if (col.filters.includes('date')) {
            return [
                { label: 'After', value: 'gt' },
                { label: 'Before', value: 'lt' },
                { label: 'Equals', value: 'equals' },
            ];
        }
        if (col.filters.includes('select')) {
            return [{ label: 'In', value: 'in' }];
        }
        return advancedFilter
            ? [
                { label: 'Contains', value: FilterMatchMode.CONTAINS },
                { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
            ]
            : [{ label: 'Contains', value: FilterMatchMode.CONTAINS }];

    }
}

export default function useDatatableFiltering(columnsRef, fetchData) {
    const filters = ref({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const advancedFilter = ref(false);
    const filtersInitialized = ref(false);
    const globalFilterFields = ref([]);

    function initFilters() {
        const initial = { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
        columnsRef.value.forEach(col => {
            if (col.filter) {
                initial[col.field] = getFilterModel(col.filters);
            }
        });
        filters.value = initial;
        filtersInitialized.value = true;
    }

    function getFilterModel(filtersParam) {
        if (!advancedFilter.value) {
            return { value: null, matchMode: FilterMatchMode.CONTAINS };
        }
        return getFilterModelBase(filtersParam);
    }

    function isFilterActive(field) {
        const filter = filters.value[field];
        if (!filter) return false;
        if (advancedFilter.value && filter.constraints) {
            return filter.constraints[0].value !== null && filter.constraints[0].value !== '';
        }
        return filter.value !== null && filter.value !== '';
    }

    function getFilterMatchModeOptions(col) {
        return getFilterMatchModeOptionsBase(col, advancedFilter.value);
    }

    // When advancedFilter toggles, reinit and fetch
    watch(advancedFilter, () => {
        initFilters();
        fetchData();
    });

    // When any filterPreset changes, same
    // (you’ll import filterPreset from useUrlPresetFilters and watch it here)

    return {
        filters,
        advancedFilter,
        filtersInitialized,
        globalFilterFields,
        initFilters,
        isFilterActive,
        getFilterMatchModeOptions,
    };
}
